/**
 * Injects a host-provided handler for inline AV clicks.
 *
 * The argument is an AV identifier in one of three shapes: a bare slug
 * (``severe_asthma_index_date``), an already-qualified knowledge id
 * (``av.<slug>``), or a library UUID (locked-AV links — OR-6115). The host
 * handler is responsible for routing each shape to the right detail record.
 *
 * Semantics:
 *   - `null` (default) → fall back to opening the knowledge-browser dialog
 *     (legacy / global-RA mount).
 *   - `(idOrSlug: string) => void` → call the handler instead. The
 *     project-page mount injects a navigate fn that routes to the variable
 *     detail route, bypassing the knowledge-corpus endpoints that are not
 *     ported.
 */

import { createContext, useContext, type ReactNode } from 'react';

type AvSlugClickHandler = ((idOrSlug: string) => void) | null;

const AvSlugClickContext = createContext<AvSlugClickHandler>(null);

interface AvSlugClickProviderProps {
  onClick: AvSlugClickHandler;
  children: ReactNode;
}

export const AvSlugClickProvider = ({ onClick, children }: AvSlugClickProviderProps) => (
  <AvSlugClickContext.Provider value={onClick}>{children}</AvSlugClickContext.Provider>
);

export const useAvSlugClick = (): AvSlugClickHandler => useContext(AvSlugClickContext);
